import { z } from 'zod';
import { ANY_BARCODE_PATTERN, BARCODE_FORMATS } from './barcode.js';
import { DocumentType, EventType, FolderStatus, LocationStatus, LocationType, SourceClient, SpineCurrency, } from './enums.js';
/**
 * Az API kontraktusa zod sémákban.
 *
 * Ugyanez a séma hajtja a backend validációt ÉS az OpenAPI dokumentumot,
 * amiből az Android kliens generálódik. Így nincs két, egymástól
 * elcsúszható igazságforrás (spec 10-11. alapelv).
 */
/**
 * Enum-objektumból zod-kompatibilis tuple.
 *
 * A visszatérési típus SZÁNDÉKOSAN a literál unió (`T[keyof T]`), nem
 * `string` — különben a `z.infer` `string`-et adna, és a service-rétegben
 * elveszne a típusbiztonság (pl. `sourceClient: string`).
 */
const enumValues = (e) => Object.values(e);
/**
 * Boolean SZÖVEGBŐL — multipart űrlapmezőhöz és query paraméterhez.
 *
 * ⚠ EZ NEM `z.coerce.boolean()`, ÉS EZ NEM STÍLUSKÉRDÉS. A `z.coerce.boolean()`
 * a JS `Boolean()`-t hívja, aminek MINDEN nem üres sztring `true` — a
 * `"false"` is. Egy `isOriginal=false` űrlapmező tehát némán `true`-ként
 * érkezett meg, és a hívó azt hitte, világosan fogalmazott.
 *
 * A hiba természete rosszabb, mint a hatása: nem jelez semmit. Az
 * újrafotózott gerincképek mind „eredetiként" kerültek volna be, és a
 * „melyik kép alapján hagyták jóvá a szöveget" kérdésre hónapokkal később
 * derült volna ki, hogy a válasz megbízhatatlan.
 *
 * Ez a séma az ISMERT alakokat fogadja el, mást pedig ELUTASÍT — egy elgépelt
 * `"igen"` inkább legyen 400-as hiba, mint csendes `true`.
 */
const textBoolean = z.preprocess((value) => {
    if (typeof value !== 'string')
        return value;
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1')
        return true;
    if (normalized === 'false' || normalized === '0')
        return false;
    return value;
}, z.boolean());
// --- Alap típusok -----------------------------------------------------------
export const folderBarcodeSchema = z
    .string()
    .regex(BARCODE_FORMATS.FOLDER.pattern, 'Érvénytelen mappa-vonalkód (várt formátum: MAP-00001).');
export const locationBarcodeSchema = z
    .string()
    .regex(ANY_BARCODE_PATTERN, 'Érvénytelen lokáció-vonalkód (várt formátum: RKL-0001, POL-0001 vagy TAR-0001).');
/**
 * Szint sorszáma szintes lokáción (raklap vagy polc).
 *
 * A felső korlát ÉPSZERŰSÉGI, nem üzleti: egy elgépelt „100" nem indíthat
 * értelmetlen műveletet. A tényleges korlátot a raklapnál a „csak a
 * legfelső fölé, ha az nem üres" szabály adja, a polcnál a létrehozáskor
 * megadott szintszám — mindkettőt a szolgáltatásréteg ellenőrzi.
 */
export const palletLevelSchema = z
    .number()
    .int()
    .min(1, 'A szint sorszáma legalább 1.')
    .max(20, 'A szint sorszáma legfeljebb 20.');
export const sourceClientSchema = z.enum(enumValues(SourceClient)).default(SourceClient.WEB);
// --- Mappa ------------------------------------------------------------------
/**
 * Új mappa regisztrálása (spec 7. § US-FOLDER-6).
 *
 * A `targetLocationBarcode` opcionális: ha nincs megadva, a backend
 * automatikusan az Előkészítőbe teszi (A változat). Ha meg van adva, a
 * regisztráció és az elhelyezés EGY tranzakcióban történik (B változat).
 */
export const createFolderSchema = z.object({
    barcode: folderBarcodeSchema,
    /** A dolgozó által ellenőrzött gerincszöveg. */
    currentText: z.string().max(4000).optional(),
    /** Opcionális azonnali elhelyezés (B változat). */
    targetLocationBarcode: locationBarcodeSchema.optional(),
    targetPalletLevel: palletLevelSchema.optional(),
    sourceClient: sourceClientSchema,
});
/** Gerincszöveg módosítása (spec 7. § US-FOLDER-4). */
export const updateFolderTextSchema = z.object({
    text: z.string().max(4000),
    /** Optimista konkurenciakezelés — a kliens által ismert verzió. */
    rowVersion: z.number().int().nonnegative(),
    isApproved: z.boolean().optional(),
    sourceClient: sourceClientSchema,
});
/**
 * A gerincadatok mezőnevei — a `SpineFields` kulcsai, futásidőben is.
 *
 * Ez a lista a `spine_manual_fields` oszlop ÉRTÉKKÉSZLETE. Azért van itt és
 * nem szabad szövegként, mert a `spineColumns()` a mezőnév alapján dönti el,
 * hogy újraszámolja-e a mezőt — egy elgépelt név csendben hatástalan
 * felülbírálást hagyna maga után.
 */
export const SPINE_FIELD_NAMES = [
    'company',
    'documentType',
    'currency',
    'yearFrom',
    'yearTo',
    'periodFrom',
    'periodTo',
    'numberFrom',
    'numberTo',
    'volume',
];
/**
 * A gerincadatok KÉZI felülbírálása (E4, 08-12 findings 2. pont).
 *
 * **Ez nem sérti a 2. alapelvet.** A gerincSZÖVEG egyetlen írója továbbra is
 * a `PUT /folders/{b}/text`; itt a szövegből SZÁRMAZTATOTT mezők javíthatók.
 * A felismerés egyiket sem írja — mindkettő emberi művelet.
 *
 * A megadott (nem `undefined`) mezők beíródnak, és a nevük bekerül a
 * `spine_manual_fields`-be: onnantól a szövegmentés újraszámolása NEM bántja
 * őket. A `resetFields` a fordított irány — kiveszi a mezőt a listából, és
 * visszaadja a parsernek. E nélkül egy elrontott felülbírálás véglegesen
 * befagyna, hiszen a `null` érték is szándékos felülbírálás lehet.
 *
 * A `null` és a hiányzó mező tehát KÜLÖNBÖZIK: `null` = „kézzel üresre
 * állítom", hiányzó = „ne nyúlj hozzá".
 */
export const updateSpineFieldsSchema = z
    .object({
    company: z.string().max(40).optional(),
    documentType: z.enum(enumValues(DocumentType)).nullable().optional(),
    currency: z.enum(enumValues(SpineCurrency)).nullable().optional(),
    yearFrom: z.number().int().min(1900).max(2200).nullable().optional(),
    yearTo: z.number().int().min(1900).max(2200).nullable().optional(),
    /** `MM` vagy `MM-DD` — hónap és dátumtartomány is kifejezhető. */
    periodFrom: z.string().max(5).nullable().optional(),
    periodTo: z.string().max(5).nullable().optional(),
    numberFrom: z.string().max(20).nullable().optional(),
    numberTo: z.string().max(20).nullable().optional(),
    volume: z.number().int().min(1).max(999).nullable().optional(),
    /** Ezek visszakerülnek az automatikus számolás alá. */
    resetFields: z.array(z.enum(SPINE_FIELD_NAMES)).optional(),
    rowVersion: z.number().int().nonnegative(),
    sourceClient: sourceClientSchema,
})
    .refine((v) => SPINE_FIELD_NAMES.some((f) => v[f] !== undefined) || (v.resetFields?.length ?? 0) > 0, { message: 'Legalább egy mezőt meg kell adni vagy visszaállítani.' });
/**
 * Mappafotó feltöltése (spec 7. § US-FOLDER-2, 14.2 §).
 *
 * A `checksum` a kliens által SZÁMOLT SHA-256 — a szerver a nyers bájtokból
 * újraszámolja és összeveti (átvitel közbeni sérülés ellen), és mellékesen
 * ez ad a multipart törzsnek is tartalom-specifikus lenyomatot az
 * idempotencia-interceptor számára (ami csak a nem-fájl mezőket látja).
 * A `contentType` és `sizeBytes` NEM a kliens állítása — a feltöltött
 * fájlból derül ki a szerveren.
 */
export const uploadFolderImageSchema = z.object({
    checksum: z
        .string()
        .regex(/^[a-f0-9]{64}$/i, 'A checksum SHA-256 hexa formátumú, 64 karakter.'),
    capturedAt: z.coerce.date(),
    /** Az eredeti gerincfotó sosem írható felül (12. alapelv) — ez jelöli. */
    isOriginal: textBoolean.default(true),
    qualityStatus: z.string().max(50).optional(),
    sourceClient: sourceClientSchema,
});
/** Mappa áthelyezése (spec 8. § US-LOCATION-2, 10. § US-FOLDER-ACTION-2). */
export const moveFolderSchema = z.object({
    targetLocationBarcode: locationBarcodeSchema,
    /** Raklap célhelynél KÖTELEZŐ, egyébként tilos. */
    targetPalletLevel: palletLevelSchema.optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Helyi (on-device ML Kit) OCR eredményének beküldése (spec 14.3 §, B6).
 *
 * A kliens ezt MÁR lefuttatta a fotózás után — ez csak az eredmény
 * rögzítése, nem indít semmilyen szerveroldali feldolgozást. A `currentText`
 * ettől NEM módosul (13. alapelv) — a kliens a szerkesztőmezőt ebből az
 * értékből töltheti elő, de a mentés emberi döntés marad.
 */
export const submitLocalOcrResultSchema = z.object({
    imageId: z.string().uuid(),
    rawText: z.string().max(4000),
    confidence: z.number().min(0).max(1).optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Sikertelen Azure AI felismerés újraindítása (spec 15.2 §, B6, US-FOLDER-5).
 * Csak `AZURE_AI` típusú, `FAILED` állapotú sorra hívható.
 */
export const retryRecognitionSchema = z.object({
    sourceClient: sourceClientSchema,
});
// --- Lokáció ----------------------------------------------------------------
/**
 * A polc szintjeinek megengedett száma (2026-08-14-i CR #14).
 *
 * A MINIMUM 2, nem 1: egy egyszintes „polc" fizikailag egy tároló, és a két
 * fogalom összemosása elvenné a különbségtétel értelmét — aki egyszintes
 * helyet akar, `TAR-`-t hozzon létre.
 *
 * A MAXIMUM 20 épszerűségi: egy elgépelt „200" húsz percre használhatatlan
 * felületet adna, és a hozzáférhetőség-számítás is értelmét vesztené.
 */
export const MIN_SHELF_LEVELS = 2;
export const MAX_SHELF_LEVELS = 20;
export const DEFAULT_SHELF_LEVELS = 5;
export const createLocationSchema = z.object({
    barcode: locationBarcodeSchema,
    name: z.string().min(1).max(200),
    locationType: z.enum(enumValues(LocationType)),
    /**
     * Csak `SHELF` típusnál: a polc szintjeinek száma.
     *
     * A polc szintjeit a szerkezete adja, ezért létrehozáskor kell megadni, és
     * mind azonnal létrejön — üresen is megmarad. A RAKLAP ezzel szemben
     * mindig egyetlen, 1-es szinttel indul, és a szintjei használat közben
     * jönnek létre; ezért nincs itt raklapra vonatkozó mező.
     *
     * Elhagyva `DEFAULT_SHELF_LEVELS` (5) — ez a raktár tipikus állványa.
     */
    levelCount: z.number().int().min(MIN_SHELF_LEVELS).max(MAX_SHELF_LEVELS).optional(),
    notes: z.string().max(2000).optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Lokáció módosítása (spec 15.3 §: „lokáció aktiválása vagy inaktiválása").
 *
 * A `locationType` szándékosan szerepel a sémában, pedig NEM módosítható:
 * ha kihagynánk, a zod csendben eldobná, és a hívó azt hinné, sikerült a
 * típusváltás. Így viszont explicit `LOCATION_TYPE_IMMUTABLE` hibát kap.
 * (A típus a vonalkód-előtaghoz és a raklap-rekordhoz is kötődik — egy
 * utólagos váltás a szintekkel és a hozzárendelésekkel is inkonzisztens
 * állapotot hagyna.)
 */
export const updateLocationSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    notes: z.string().max(2000).nullable().optional(),
    status: z.enum(enumValues(LocationStatus)).optional(),
    locationType: z.enum(enumValues(LocationType)).optional(),
    sourceClient: sourceClientSchema,
});
export const createPrinterSchema = z.object({
    printnodeId: z.number().int().positive(),
    name: z.string().min(1).max(200),
    /** Igaz esetén ez lesz AZ aktív nyomtató, a korábbi kikapcsol. */
    isActive: z.boolean().default(false),
    sourceClient: sourceClientSchema,
});
export const updatePrinterSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    isActive: z.boolean().optional(),
    rowVersion: z.number().int().nonnegative(),
    sourceClient: sourceClientSchema,
});
export const createSpineIgnorePatternSchema = z.object({
    /** A szerver normalizálja (kisbetű, ékezet nélkül) tárolás előtt. */
    pattern: z.string().min(3).max(200),
    note: z.string().max(500).optional(),
    sourceClient: sourceClientSchema,
});
export const updateSpineIgnorePatternSchema = z.object({
    note: z.string().max(500).nullable().optional(),
    isActive: z.boolean().optional(),
    rowVersion: z.number().int().nonnegative(),
    sourceClient: sourceClientSchema,
});
export const createCompanySchema = z.object({
    /** Nagybetűs, beszédes kód — ez kerül a `folder.spine_company` oszlopba. */
    code: z
        .string()
        .min(2)
        .max(40)
        .regex(/^[A-Za-z0-9_]+$/, 'A cégkód csak betűt, számot és aláhúzást tartalmazhat.'),
    name: z.string().min(1).max(200),
    /** Ahogy a gerincen szerepelhet — a szerver normalizálja tárolás előtt. */
    aliases: z.array(z.string().min(1).max(100)).max(20).default([]),
    sourceClient: sourceClientSchema,
});
/**
 * Cég módosítása.
 *
 * A `code` SZÁNDÉKOSAN nem módosítható: az a `folder.spine_company` idegen
 * kulcsa, tehát az átírása minden érintett mappát átsorolna — egy átnevezés
 * a `name` mezőn keresztül megy, ami nem hordoz hivatkozást.
 *
 * Az `aliases` megadása TELJES CSERE, nem hozzáfűzés.
 */
export const updateCompanySchema = z.object({
    name: z.string().min(1).max(200).optional(),
    isActive: z.boolean().optional(),
    aliases: z.array(z.string().min(1).max(100)).max(20).optional(),
    rowVersion: z.number().int().nonnegative(),
    sourceClient: sourceClientSchema,
});
/**
 * A gerincadatok újraelemzése (F2).
 *
 * `dryRun: true` esetén CSAK számol — semmit nem ír. Egy elgépelt alias így
 * nem sorol át némán száz mappát; előbb látszik, hányat érintene.
 */
export const reparseSpineSchema = z.object({
    dryRun: z.boolean().default(true),
    sourceClient: sourceClientSchema,
});
// --- Lekérdezések -----------------------------------------------------------
/**
 * Rendezési szempontok a mappalistán (D fázis, 7.1 pont).
 *
 * Zárt halmaz, nem szabad szöveg: egy nyers oszlopnév átvétele SQL-injekciós
 * felület lenne, és olyan oszlopokra is engedne rendezni, amikre nincs index.
 */
export const FOLDER_SORT_FIELDS = [
    'barcode',
    'createdAt',
    'updatedAt',
    'spineCompany',
    'spineDocumentType',
    'spineYearFrom',
];
export const folderQuerySchema = z.object({
    /** Vonalkód-részlet vagy gerincszöveg. */
    query: z.string().max(200).optional(),
    status: z.enum(enumValues(FolderStatus)).optional(),
    locationBarcode: locationBarcodeSchema.optional(),
    /**
     * Gerincmező-szűrők (C3 befejezése).
     *
     * A `folder` tábla ezen oszlopai INDEXELVE vannak, de eddig nem volt
     * hozzájuk sem API-szűrő, sem felület — a strukturált mezők csak
     * megjelentek, keresni nem lehetett rájuk. A `search_vector` SZÁNDÉKOSAN
     * nem tartalmazza őket (generált oszlop kifejezése nem lehet `enum::text`,
     * mert az csak STABLE, nem IMMUTABLE); a szűrés helyette indexelt
     * egyenlőség-vizsgálattal megy.
     */
    company: z.string().max(40).optional(),
    documentType: z.enum(enumValues(DocumentType)).optional(),
    /** Az évtartomány ÁTFEDÉSÉRE szűr, nem pontos egyezésre. */
    yearFrom: z.coerce.number().int().min(1900).max(2200).optional(),
    yearTo: z.coerce.number().int().min(1900).max(2200).optional(),
    sortBy: z.enum(FOLDER_SORT_FIELDS).default('barcode'),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
/**
 * Rendezhető oszlopok a lokáció-/raklaplistán (F5, 08-13 CR 8.1).
 *
 * A `folderCount` SZÁNDÉKOSAN nincs benne: az nem oszlop, hanem az aktív
 * hozzárendelések összesítése — szerveroldali rendezéshez a teljes lista
 * megszámolása kellene minden kéréshez. Ha valaha kell, az egy denormalizált
 * számláló oszlop kérdése, nem egy `orderBy`-é.
 */
export const LOCATION_SORT_FIELDS = ['barcode', 'name', 'locationType', 'createdAt'];
export const locationQuerySchema = z.object({
    locationType: z.enum(enumValues(LocationType)).optional(),
    status: z.enum(enumValues(LocationStatus)).optional(),
    query: z.string().max(200).optional(),
    sortBy: z.enum(LOCATION_SORT_FIELDS).default('barcode'),
    sortDir: z.enum(['asc', 'desc']).default('asc'),
});
/**
 * Globális eseménynapló-lekérdezés (spec 12. §, B7) — a mappa- és
 * raklap-előzménytől eltérően NEM egy entitáshoz kötött, hanem
 * kereszt-entitású, admin/audit célú szűrés.
 */
export const eventQuerySchema = z.object({
    eventType: z.enum(enumValues(EventType)).optional(),
    /**
     * Egy tömeges művelet ÖSSZES eseménye (spec 12. §: „Egy tömeges művelet
     * eseményei közös korrelációs azonosítóval összekapcsolhatók").
     * A címkekötegnél ez a köteg azonosítója — a `LabelBatchSummary.id`
     * doc-commentje régóta ígérte ezt a szűrőt, de nem létezett.
     */
    batchId: z.string().uuid().optional(),
    folderBarcode: folderBarcodeSchema.optional(),
    palletBarcode: locationBarcodeSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    /**
     * Csak egy ADOTT ESZKÖZ eseményei (G6, CR #17).
     *
     * A TC22 főképernyőjének naplója ezzel szűr — a dolgozónak az a
     * visszaigazolás kell, hogy amit ő, ezen a gépen csinált, azt a szerver
     * elfogadta.
     */
    deviceId: z.string().max(64).optional(),
    /**
     * Csak a HÍVÓ saját eseményei.
     *
     * Szándékosan boolean, nem `userId`: a felhasználót sosem a kliens mondja
     * meg (spec 6. § AC5). A szerver a validált tokenből tölti ki.
     */
    mine: textBoolean.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
/** Az elsődleges kép kijelölése (G4, 2026-08-14-i CR #1). */
export const setPrimaryImageSchema = z.object({
    sourceClient: sourceClientSchema,
});
/**
 * NEM elsődleges kép törlése (G4) — a 12. alapelv szűk, tudatos kivétele.
 *
 * Az indok KÖTELEZŐ, mert ez az egyetlen pont, ahol a rendszerből tény tűnik
 * el: a miértnek akkor is meg kell maradnia a naplóban, ha a kép már nincs.
 * A tipikus eset GDPR — véletlenül egy kolléga arca került a képre.
 */
export const deleteImageSchema = z.object({
    reason: z.string().min(1).max(500),
    sourceClient: sourceClientSchema,
});
// --- Raklapműveletek (spec 9. és 11. §) -------------------------------------
/**
 * Mappa hozzárendelése egy raklapszinthez (spec 9. § US-PALLET-1, US-PALLET-2).
 *
 * Ez a rendszer legnagyobb volumenű írási művelete: a tömeges feltöltésnél
 * mappánként egyszer fut le. Ezért SZÁNDÉKOSAN nem atomi az egész munkamenet,
 * csak mappánként — a US-PALLET-2 AC8 megköveteli, hogy megszakadt
 * munkamenetnél a már mentett hozzárendelések ne vesszenek el.
 */
export const assignToPalletLevelSchema = z.object({
    folderBarcode: folderBarcodeSchema,
    /** A munkamenet azonosítója; a kliens generálja szintenként (9. § AC9). */
    batchId: z.string().uuid().optional(),
    /**
     * Ha a mappa MÁS aktív lokáción van, alapból hibát adunk, hogy a dolgozó
     * lássa hol van (9. § AC4). Ezzel a kapcsolóval erősíthető meg a mozgatás.
     */
    allowMoveFromOtherLocation: z.boolean().default(false),
    sourceClient: sourceClientSchema,
});
/**
 * Új szint hozzáadása egy szintes lokációhoz.
 *
 * A név szándékosan „hozzáadás", nem „nyitás": a 2026-08-13-i CR 1.1 óta a
 * szintnek nincs nyitott/zárt állapota, a régi szóhasználat viszont bent
 * maradt a TC22 feliratain, és a megrendelő jogosan hitte, hogy a
 * megszüntetett funkció még él (2026-08-14-i CR #10).
 */
export const openLevelSchema = z.object({
    reason: z.string().max(500).optional(),
    sourceClient: sourceClientSchema,
});
/**
 * A LEGFELSŐ, ÜRES szint törlése (G1).
 *
 * Csak a legfelső: egy közbenső szint eltávolítása a fölötte lévőket is
 * átszámozná, ami a fizikai valóságtól függetlenül írná át, hogy egy mappa
 * hányadik szinten van. A raklapnál a kiürülő KÖZBENSŐ szint amúgy is
 * magától összecsukódik (`PALLET_LEVEL_COLLAPSED`) — ez a végpont arra az
 * egyetlen esetre kell, amit az nem fed: a legfelső, üres szintre.
 */
export const removeLevelSchema = z.object({
    reason: z.string().max(500).optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Polcszint lezárása / feloldása (G1) — KIZÁRÓLAG polcon.
 *
 * ⚠ Ez nem a 2026-08-13-i CR 1.1-ben megszüntetett szintstátusz: az a
 * RAKLAPszintekről szólt, ahol a nyitás/zárás üres adminisztratív lépés
 * volt egy fizikailag nem létező állapotról. A polc szerkezete fix, ezért
 * ott értelmes kimondani, hogy egy szintre átmenetileg nem tárolunk be.
 *
 * A lezárás CSAK a betárolást tiltja, a kivételt SOSEM.
 */
export const lockLevelSchema = z.object({
    reason: z.string().max(500).optional(),
    sourceClient: sourceClientSchema,
});
export const closeLevelSchema = z.object({
    /** A munkamenet azonosítója, hogy a lezárás összefűzhető legyen a feltöltéssel. */
    batchId: z.string().uuid().optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Raklap vagy raklapszint felbontása (spec 11. § US-PALLET-ACTION-2/3).
 *
 * A felbontás SZIGORÚAN ATOMI: "vagy minden mappa átkerül, vagy egyik sem".
 * A célhely kötelező — a mappa nem maradhat lokáció nélkül (3. alapelv).
 */
// A `placeOnShelfSchema` / `removeFromShelfSchema` MEGSZŰNT (G fázis).
//
// Az E1-ben a raklap betárolható volt egy polcszintre, és ez nem mozdította
// a rajta lévő mappákat. Működött, de a mappa helye ettől két lépésből állt
// (`MAP-00123 → RKL-0007/2 → POL-0003/3`), és minden „hol van" kérdésre két
// úton lehetett válaszolni. A megrendelő 2026-08-14-én elvetette: mindhárom
// lokációtípus VÉGSŐ, egymásba ágyazás nincs.
export const dismantleSchema = z.object({
    targetLocationBarcode: locationBarcodeSchema,
    targetPalletLevel: palletLevelSchema.optional(),
    /**
     * A kliens által látott mappaszám. Ha eltér a szerverétől, a művelet
     * elutasításra kerül — így nem bontunk fel olyan raklapot, ami közben
     * megváltozott a megerősítő képernyő megjelenítése óta.
     */
    expectedFolderCount: z.number().int().nonnegative().optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Mappa hozzárendelése BÁRMILYEN lokációhoz (2026-08-25).
 *
 * Ugyanaz, mint az [assignToPalletLevelSchema], egy mezővel bővítve: a szint
 * a bodyban utazik, és `null` lehet. A tárolónak nincs szintje, a raklapnak
 * és a polcnak van — hogy melyik a helyes, azt a `resolveTarget` mondja meg,
 * tehát a kliens nem tud némán rossz helyre pakolni.
 *
 * MIÉRT KELL EGYÁLTALÁN: a tömeges betárolás eddig kizárólag a
 * `/pallets/{b}/levels/{n}/folders` végponton létezett, ezért a TC22-n a polc
 * CSAK OLVASHATÓ volt, a tárolóhoz pedig egyáltalán nem tartozott képernyő —
 * pedig a `TAR-0001` Előkészítőbe esik minden új regisztráció, tehát az a
 * legforgalmasabb lokáció az egész rendszerben.
 */
export const assignToLocationSchema = assignToPalletLevelSchema.extend({
    levelNumber: z.number().int().positive().nullable().optional(),
});
/**
 * MINDEN MAPPA ÁTHELYEZÉSE egy lokációról vagy annak egy szintjéről.
 *
 * `levelNumber == null` → az EGÉSZ lokációról. A raklapnál ilyenkor az 1.
 * szint fölötti szintek is megszűnnek (az elválasztó lemezek lekerülnek); a
 * polcnál NEM, mert a polc szerkezete fix — egy kiürült polcszint továbbra is
 * létezik, csak üres.
 */
export const moveAllFromLocationSchema = dismantleSchema.extend({
    levelNumber: z.number().int().positive().nullable().optional(),
});
/**
 * Mappacímkék foglalása és nyomtatása (B9). A `count` alapértéke 100 — egy
 * kényelmes nyomtatási menet, és egy elrontott köteg a 4800 fizikai
 * címkéből képzett ~800-as tartalék kezelhető töredéke.
 */
export const printFolderLabelsSchema = z.object({
    count: z.number().int().min(1).max(5000).default(100),
    note: z.string().max(200).optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Raklap- és lokációcímkék nyomtatása — kiosztás NÉLKÜL, mert ezek a
 * `location` sorok már léteznek (a raklapcímke a fordított aszimmetria).
 */
export const printLocationLabelsSchema = z.object({
    barcodes: z.array(locationBarcodeSchema).min(1).max(200),
    note: z.string().max(200).optional(),
    sourceClient: sourceClientSchema,
});
/**
 * Lokációcímke-TARTOMÁNY nyomtatása (2026-08-14-i CR #23).
 *
 * A hiányzó lokációkat LÉTREHOZZA, mielőtt nyomtatna — a fenti végpont
 * ezzel szemben csak meglévő sorokra megy. Ez a különbség a lényeg: az „50
 * üres raklapra felragasztom az RKL-0001…0050-et" munkafolyamathoz előbb
 * kell a szám, aztán az objektum, ahogy a mappacímkénél is.
 *
 * A már létező lokációkat NEM bántja (se át nem nevezi, se nem duplikálja) —
 * így egy megismételt nyomtatás ártalmatlan.
 */
export const printLocationRangeSchema = z
    .object({
    locationType: z.enum(enumValues(LocationType)),
    fromSequence: z.number().int().min(1).max(9999),
    toSequence: z.number().int().min(1).max(9999),
    /** A létrehozott lokációk neve: `${namePrefix} ${sorszám}`. */
    namePrefix: z.string().min(1).max(150).optional(),
    /** Csak `SHELF`-nél: az újonnan létrehozott polcok szintszáma. */
    levelCount: z.number().int().min(MIN_SHELF_LEVELS).max(MAX_SHELF_LEVELS).optional(),
    note: z.string().max(200).optional(),
    sourceClient: sourceClientSchema,
})
    .refine((v) => v.toSequence >= v.fromSequence, {
    message: 'A toSequence nem lehet kisebb, mint a fromSequence.',
    path: ['toSequence'],
})
    // A 200-as korlát a `printLocationLabelsSchema`-val egyezik: egy köteg egy
    // nyomtatási menet, és egy elgépelt „1-9999" nem indíthat tízezer címkét.
    .refine((v) => v.toSequence - v.fromSequence + 1 <= 200, {
    message: 'Egyszerre legfeljebb 200 lokációcímke nyomtatható.',
    path: ['toSequence'],
});
/**
 * Selejtezett sorszám visszaadása a forgalomba (2026-08-14-i CR #22).
 *
 * Kockázatmentes, mert a selejtezés eleve CSAK olyan kiosztást érint,
 * amihez nem tartozik mappa — egy visszaadott számra tehát fogalmilag sosem
 * történt regisztráció, és nem lesz két fizikai tárgy ugyanazon az
 * azonosítón. Ezt a szerver újra ellenőrzi, nem hisz a történetnek.
 */
export const restoreAllocationSchema = z.object({
    reason: z.string().min(1).max(200),
    sourceClient: sourceClientSchema,
});
/**
 * Ugyanazon köteg újraküldése a nyomtatóra — NEM foglal új sorszámot, tehát
 * nem hagy hézagot. A from/toBarcode a csonka vég pótlására, hogy egy
 * elakadt szalag ne vigye kárba a fél köteg fizikai címkét.
 */
export const resendLabelBatchSchema = z.object({
    fromBarcode: z.string().regex(ANY_BARCODE_PATTERN).optional(),
    toBarcode: z.string().regex(ANY_BARCODE_PATTERN).optional(),
    reason: z.string().min(1).max(200),
    sourceClient: sourceClientSchema,
});
/** Ember erősíti meg, hogy a köteg fizikailag kinyomtatva a kezében van. */
export const confirmLabelBatchSchema = z.object({
    sourceClient: sourceClientSchema,
});
/**
 * Egy köteg selejtezése. A már REGISZTRÁLT mappához tartozó sorszámokat a
 * szolgáltatás kihagyja — egy mappára felragasztott címke visszamenőleg
 * nem érvényteleníthető.
 */
export const voidLabelBatchSchema = z.object({
    reason: z.string().min(1).max(200),
    sourceClient: sourceClientSchema,
});
/**
 * Üzemeltetői vészkijárat: sorszám(ok) foglalása nyomtatás NÉLKÜL. Ez teszi
 * vállalhatóvá a `folder.barcode` → `label_allocation` idegen kulcsot: ha
 * egy KONKRÉT, a rendszer mellett már kinyomtatott/felragasztott címke
 * bukkan fel a raktárban, ez az admin művelet ~10 másodperc alatt feloldja
 * a regisztrációs blokádot. Ezért `fromSequence`/`toSequence` — nem
 * `count` —: nem "adj néhány új számot", hanem "vezesd be pontosan EZT a
 * már létező sorszámot/tartományt". Ütközésnél (a szám már ki van osztva)
 * a hiba a HELYES válasz.
 */
export const allocateLabelsSchema = z
    .object({
    fromSequence: z.number().int().min(1).max(99_999),
    toSequence: z.number().int().min(1).max(99_999),
    reason: z.string().min(1).max(200),
    sourceClient: sourceClientSchema,
})
    .refine((v) => v.toSequence >= v.fromSequence, {
    message: 'A toSequence nem lehet kisebb, mint a fromSequence.',
    path: ['toSequence'],
})
    .refine((v) => v.toSequence - v.fromSequence + 1 <= 5000, {
    message: 'Egyszerre legfeljebb 5000 sorszám foglalható.',
    path: ['toSequence'],
});
/**
 * Sérült címke újranyomtatása — KIZÁRÓLAG már regisztrált mappára, sosem
 * tömegesen. A `confirmedImageId`/`rowVersion` a UI-megerősítést szerver
 * által kikényszerített invariánssá fordítja: ha a gerincszöveg vagy a kép
 * közben megváltozott, a dolgozó elavult információ alapján erősített meg.
 */
export const reprintFolderLabelSchema = z.object({
    confirmedImageId: z.string().uuid(),
    rowVersion: z.number().int().nonnegative(),
    reason: z.string().min(1).max(200),
    sourceClient: sourceClientSchema,
});
export const labelBatchQuerySchema = z.object({
    printStatus: z.enum(['PENDING', 'SENT', 'UNCONFIRMED', 'FAILED', 'CONFIRMED', 'NOT_PRINTED']).optional(),
    labelKind: z.enum(['FOLDER', 'PALLET', 'SHELF', 'STORAGE']).optional(),
    includeVoided: textBoolean.default(false),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
//# sourceMappingURL=api.js.map